import React, { useEffect, useRef, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Search, Upload, X, ImageIcon, Loader2, Trash2 } from 'lucide-react';
import {
  buildAssetUrl,
  createAdminEntity,
  deleteAdminEntity,
  fetchAdminList,
  friendlyError,
  updateAdminEntity,
  uploadAdminImage,
} from '@/lib/api';
import {
  extractFieldErrors, fieldLabelsFromFormFields, DEFAULT_FORM_ERROR_MESSAGE,
} from '@/lib/formErrors';
import { toast } from 'sonner';

// ---------------------------------------------------------------
// ---------------------------------------------------------------
// Formato automático de texto libre (Fase 04.2)
//
// Regla: en campos de texto libre (descripciones, notas, contenido
// editorial, textos informativos) se aplica sentence-case al perder
// el foco y al guardar:
//   "PARTIDO DESTACADO DE LA JORNADA" → "Partido destacado de la jornada"
//
// Detección automática:
//   - Tipo `textarea` → free text por defecto.
//   - Nombres del campo que casan con patrones de contenido libre
//     (description / notes / summary / content / bio / info / editorial
//      / excerpt / about / message).
//
// Se puede forzar/quitar con `freeText: true | false` en la definición.
// NO se aplica a campos estructurados (nombres propios, ciudades,
// países, slugs, URLs, etc.) que se preservan tal cual los escribe
// el usuario.
// ---------------------------------------------------------------
const FREE_TEXT_NAME_PATTERNS = [
  /description/i,
  /notes?$/i,
  /summary/i,
  /content/i,
  /^bio$/i,
  /info$/i,
  /editorial/i,
  /excerpt/i,
  /about/i,
  /message/i,
];

function isFreeTextField(field) {
  if (!field) return false;
  if (field.freeText === false) return false;
  if (field.freeText === true) return true;
  if (field.type === 'textarea') return true;
  if (FREE_TEXT_NAME_PATTERNS.some((re) => re.test(field.name))) return true;
  return false;
}

function toSentenceCase(value) {
  if (typeof value !== 'string') return value;
  if (!value.trim()) return value;
  const lower = value.toLowerCase();
  // Capitaliza la primera LETRA real, saltando espacios, dígitos o puntuación iniciales.
  return lower.replace(/^([^\p{L}]*)(\p{L})/u, (_, prefix, ch) => prefix + ch.toUpperCase());
}

function applySentenceCaseToPayload(payload, formFields) {
  const out = { ...payload };
  formFields.forEach((f) => {
    if (!isFreeTextField(f)) return;
    const v = out[f.name];
    if (typeof v === 'string' && v) {
      out[f.name] = toSentenceCase(v);
    }
  });
  return out;
}

function StatusBadge({ status }) {
  const active = status === 'active';
  return (
    <Badge
      variant="secondary"
      className={
        active
          ? 'bg-[var(--ns-green)]/10 text-[var(--ns-green-700)] border border-[var(--ns-green)]/30'
          : 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border border-[var(--ns-border)]'
      }
    >
      {status || 'active'}
    </Badge>
  );
}

function ImageUploadField({ field, value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = value ? buildAssetUrl(value) : '';
  const kind = field.uploadKind || 'thumbnail';
  const aspect = field.aspectClass || 'aspect-video';
  // Logos / sponsors: mantener proporción completa con padding (object-contain).
  // Cover / thumbnail: pueden recortarse (object-cover) para llenar el contenedor.
  const isLogoKind = kind === 'logo' || kind === 'sponsor';
  const imgClass = isLogoKind
    ? 'max-h-full max-w-full w-auto h-auto object-contain'
    : 'w-full h-full object-cover';
  const containerPad = isLogoKind ? 'p-2' : '';

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      toast.error('Solo se permiten imágenes JPG o PNG.');
      return;
    }
    setBusy(true);
    try {
      const data = await uploadAdminImage(file, kind);
      onChange(data.url);
      toast.success('Imagen subida.');
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[var(--ns-text)]">
        {field.label}{field.required ? <span className="text-[var(--ns-red)] ml-1">*</span> : null}
      </Label>
      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <div
          className={`${aspect} ${containerPad} w-full sm:w-48 shrink-0 rounded-xl overflow-hidden border border-dashed border-[var(--ns-border)] bg-[var(--ns-bg-2)] flex items-center justify-center relative`}
          data-testid={`admin-field-${field.name}-preview`}
        >
          {preview ? (
            <img src={preview} alt={field.label} className={imgClass} />
          ) : (
            <div className="text-[var(--ns-text-subtle)] flex flex-col items-center gap-1">
              <ImageIcon className="h-6 w-6" />
              <span className="text-[10px] uppercase tracking-wider">Sin imagen</span>
            </div>
          )}
          {busy ? (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <Loader2 className="h-6 w-6 text-white animate-spin" />
            </div>
          ) : null}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`admin-field-${field.name}-file`}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="h-9 rounded-xl"
              data-testid={`admin-field-${field.name}-upload`}
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {preview ? 'Reemplazar' : 'Subir imagen'}
            </Button>
            {preview ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange('')}
                disabled={busy}
                className="h-9 rounded-xl text-[var(--ns-red)] hover:bg-[var(--ns-red)]/10"
                data-testid={`admin-field-${field.name}-clear`}
              >
                <X className="h-3.5 w-3.5 mr-1.5" />
                Quitar
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-[var(--ns-text-subtle)]">
            {field.hint || 'JPG o PNG. Se redimensionará y optimizará automáticamente.'}
          </p>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, allValues = {}, error }) {
  if (field.hidden) return null;
  if (typeof field.dependsOn === 'function' && !field.dependsOn(allValues)) return null;
  // Clase de borde rojo cuando el campo tiene error de validación.
  const invalid = Boolean(error);
  const invalidBorder = invalid ? 'border-[var(--ns-red,#dc2626)] focus-visible:ring-red-200' : '';
  const errorNode = invalid ? (
    <p
      className="text-xs text-[var(--ns-red,#dc2626)]"
      role="alert"
      data-testid={`admin-field-${field.name}-error`}
    >
      {error}
    </p>
  ) : null;
  if (field.type === 'image') {
    return (
      <div className="space-y-1.5">
        <ImageUploadField field={field} value={value} onChange={onChange} />
        {errorNode}
      </div>
    );
  }
  // ----- Readonly: valor de solo lectura (métricas internas, contadores, etc.). -----
  if (field.type === 'readonly') {
    const display = field.format ? field.format(value) : (value ?? '—');
    return (
      <div
        className="text-sm text-[var(--ns-text)]"
        data-testid={`admin-field-${field.name}-readonly`}
      >
        {display}
      </div>
    );
  }
  // ----- Money: input en euros, almacena céntimos (number) en form. -----
  if (field.type === 'money') {
    // value en form = céntimos (number) o ''. Mostramos como "2,50".
    const centsToEur = (cents) => {
      if (cents === '' || cents === null || cents === undefined) return '';
      const n = Number(cents);
      if (Number.isNaN(n)) return '';
      return (n / 100).toFixed(2).replace('.', ',');
    };
    const eurStrToCents = (s) => {
      if (typeof s !== 'string') return '';
      const cleaned = s.replace(/\s|€/g, '').replace(/,/g, '.');
      if (cleaned === '') return '';
      const n = Number(cleaned);
      if (Number.isNaN(n) || n < 0) return '';
      return Math.round(n * 100);
    };
    return (
      <div className="space-y-1.5">
        <Label className={`text-sm font-medium ${invalid ? 'text-[var(--ns-red,#dc2626)]' : 'text-[var(--ns-text)]'}`}>
          {field.label}{field.required ? <span className="text-[var(--ns-red)] ml-1">*</span> : null}
        </Label>
        <div className="relative">
          <Input
            type="text"
            inputMode="decimal"
            value={centsToEur(value)}
            onChange={(e) => onChange(eurStrToCents(e.target.value))}
            placeholder={field.placeholder || '2,50'}
            aria-invalid={invalid || undefined}
            className={`h-10 rounded-xl bg-white border-[var(--ns-border)] pr-9 ${invalidBorder}`}
            data-testid={`admin-field-${field.name}`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--ns-text-muted)] pointer-events-none">€</span>
        </div>
        {field.hint ? <p className="text-xs text-[var(--ns-text-subtle)]">{field.hint}</p> : null}
        {errorNode}
      </div>
    );
  }
  const applyOnBlur = isFreeTextField(field)
    ? () => {
        if (typeof value === 'string' && value && value.trim()) {
          const next = toSentenceCase(value);
          if (next !== value) onChange(next);
        }
      }
    : undefined;
  const label = (
    <Label className={`text-sm font-medium ${invalid ? 'text-[var(--ns-red,#dc2626)]' : 'text-[var(--ns-text)]'}`}>
      {field.label}{field.required ? <span className="text-[var(--ns-red)] ml-1">*</span> : null}
    </Label>
  );
  if (field.type === 'select') {
    const options = typeof field.options === 'function' ? field.options(allValues) : (field.options || []);
    return (
      <div className="space-y-1.5">
        {label}
        <Select value={value || ''} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
          <SelectTrigger
            aria-invalid={invalid || undefined}
            className={`h-10 rounded-xl bg-white border-[var(--ns-border)] ${invalidBorder}`}
            data-testid={`admin-field-${field.name}`}
          >
            <SelectValue placeholder={field.placeholder || 'Selecciona…'} />
          </SelectTrigger>
          <SelectContent>
            {!field.required ? <SelectItem value="__none__">— Ninguno —</SelectItem> : null}
            {options.map((o) => (
              <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.hint ? <p className="text-xs text-[var(--ns-text-subtle)]">{field.hint}</p> : null}
        {errorNode}
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="space-y-1.5">
        {label}
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={applyOnBlur}
          placeholder={field.placeholder}
          rows={field.rows || 4}
          aria-invalid={invalid || undefined}
          className={`rounded-xl bg-white border-[var(--ns-border)] ${invalidBorder}`}
          data-testid={`admin-field-${field.name}`}
        />
        {field.hint ? <p className="text-xs text-[var(--ns-text-subtle)]">{field.hint}</p> : null}
        {errorNode}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {label}
      <Input
        type={field.type || 'text'}
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => onChange(field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
        onBlur={applyOnBlur}
        placeholder={field.placeholder}
        aria-invalid={invalid || undefined}
        className={`h-10 rounded-xl bg-white border-[var(--ns-border)] ${invalidBorder}`}
        data-testid={`admin-field-${field.name}`}
      />
      {field.hint ? <p className="text-xs text-[var(--ns-text-subtle)]">{field.hint}</p> : null}
      {errorNode}
    </div>
  );
}

/**
 * AdminCrudShell
 * Componente genérico para gestionar entidades admin con tabla + form drawer.
 *
 * Props:
 * - title, subtitle, entity (clave en /admin/{entity}/)
 * - columns: [{ key, label, render?: (row)=>node, className? }]
 * - formFields: [{ name, label, type, required?, options?, hint?, placeholder?, dependsOn?(values) }]
 * - searchPlaceholder, initialForm, beforeSubmit?(values)=>values
 * - canCreate (default true)
 * - extraFilters?: render function for additional toolbar items
 * - dataDeps?: list of values that force a reload when changed
 */
export function AdminCrudShell({
  title,
  subtitle,
  entity,
  columns,
  formFields = [],
  searchPlaceholder = 'Buscar…',
  initialForm = {},
  beforeSubmit,
  canCreate = true,
  extraQueryParams = {},
  testIdPrefix = 'admin-crud',
  readOnly = false,
  filters = null,
  addLabel = 'Nuevo',
  rowActions = null,
  canDelete = true,
  rowLabel = null,
  deleteWarning = null,
  fieldErrorOverrides = {},
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // Debounce 350ms del término para evitar una petición por tecla cuando
  // hay miles de registros (ver /admin/suscripciones y /admin/compras).
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...initialForm });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);
  const [filterValues, setFilterValues] = useState(() => {
    const init = {};
    (filters || []).forEach((f) => { init[f.name] = ''; });
    return init;
  });

  const showCreate = canCreate && !readOnly;
  const showSearch = !readOnly || !!searchPlaceholder;

  const reload = async () => {
    setLoading(true);
    try {
      const params = { ...extraQueryParams };
      if (debouncedQ) params.q = debouncedQ;
      (filters || []).forEach((f) => {
        if (filterValues[f.name]) params[f.name] = filterValues[f.name];
      });
      const res = await fetchAdminList(entity, params);
      setItems(res.items || []);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [debouncedQ, refreshTick, JSON.stringify(extraQueryParams), JSON.stringify(filterValues)]);

  // Auto-reset de valores de filtro dependientes: si el valor actualmente
  // seleccionado deja de estar entre las opciones del filtro (p. ej. porque
  // otro filtro superior lo excluyó), lo resetea a "todos" para evitar
  // consultas con IDs huérfanos.
  useEffect(() => {
    (filters || []).forEach((f) => {
      const val = filterValues[f.name];
      if (!val) return;
      const opts = f.options || [];
      const exists = opts.some((o) => String(o.value) === String(val));
      if (!exists) {
        setFilterValues((prev) => ({ ...prev, [f.name]: '' }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((filters || []).map((f) => ({ n: f.name, o: (f.options || []).map((o) => o.value) })))]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...initialForm });
    setFieldErrors({});
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const next = { ...initialForm };
    formFields.forEach((f) => { next[f.name] = row[f.name] ?? ''; });
    setForm(next);
    setFieldErrors({});
    setOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let payload = { ...form };
      // Sentence-case en campos de texto libre (descripciones, notas, etc.)
      payload = applySentenceCaseToPayload(payload, formFields);
      // Limpiar strings vacíos a undefined para campos no required
      formFields.forEach((f) => {
        if (!f.required && (payload[f.name] === '' || payload[f.name] === null)) {
          delete payload[f.name];
        }
      });
      if (beforeSubmit) payload = beforeSubmit(payload, { editing });
      if (editing) {
        const { message } = await updateAdminEntity(entity, editing.id, payload);
        toast.success(message || 'Actualizado.');
      } else {
        const { message } = await createAdminEntity(entity, payload);
        toast.success(message || 'Creado.');
      }
      setFieldErrors({});
      setOpen(false);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      // Fase 09.1 · Errores de validación por campo
      // -----------------------------------------------------------------
      // Si el backend devuelve un 422 pydantic (envoltura ApiResponse con
      // `data: [{loc, msg, type}, ...]`), lo traducimos a mensajes humanos
      // y los pintamos inline en el campo correspondiente. El toast muestra
      // el primer mensaje concreto para evitar el genérico "Los datos
      // enviados no son válidos" cuando sabemos la causa real.
      const labels = fieldLabelsFromFormFields(formFields);
      const parsed = extractFieldErrors(err, {
        fieldLabels: labels,
        overrides: fieldErrorOverrides,
      });
      if (parsed.hasFieldErrors) {
        setFieldErrors(parsed.fieldErrors);
        toast.error(parsed.formMessage || DEFAULT_FORM_ERROR_MESSAGE);
      } else {
        setFieldErrors({});
        toast.error(parsed.formMessage || friendlyError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -------- Eliminación (soft delete) --------
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const askDelete = (row) => setDeleteTarget(row);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { message } = await deleteAdminEntity(entity, deleteTarget.id);
      toast.success(message || 'Eliminado.');
      setDeleteTarget(null);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setDeleting(false);
    }
  };

  const targetLabel = (() => {
    if (!deleteTarget) return '';
    if (typeof rowLabel === 'function') return rowLabel(deleteTarget);
    return deleteTarget.title || deleteTarget.name || deleteTarget.label
      || deleteTarget.email || deleteTarget.city || deleteTarget.id;
  })();

  const total = items.length;

  return (
    <AdminLayout>
      <div className="max-w-6xl" data-testid={`${testIdPrefix}-page`}>
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold">
              Gestión admin
            </p>
            <h1 className="mt-1 font-display text-2xl sm:text-3xl text-[var(--ns-text)] leading-tight">{title}</h1>
            {subtitle ? <p className="mt-2 text-sm text-[var(--ns-text-muted)]">{subtitle}</p> : null}
          </div>
          {showCreate ? (
            <Button
              onClick={openNew}
              className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white transition-all"
              data-testid={`${testIdPrefix}-new-btn`}
            >
              <Plus className="h-4 w-4 mr-2" /> {addLabel}
            </Button>
          ) : null}
        </header>

        <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:flex-wrap">
          {showSearch ? (
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ns-text-subtle)]" />
              <Input
                type="search"
                placeholder={searchPlaceholder}
                className="h-10 pl-10 rounded-xl bg-white border-[var(--ns-border)]"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
          ) : null}
          {(filters || []).map((f) => (
            <Select
              key={f.name}
              value={filterValues[f.name] || '__all__'}
              onValueChange={(v) => {
                const value = v === '__all__' ? '' : v;
                setFilterValues((prev) => ({ ...prev, [f.name]: value }));
                if (typeof f.onChange === 'function') f.onChange(value);
              }}
            >
              <SelectTrigger
                className="h-10 rounded-xl bg-white border-[var(--ns-border)] w-full sm:w-44"
                data-testid={`${testIdPrefix}-filter-${f.name}`}
              >
                <SelectValue placeholder={f.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{f.label}: todos</SelectItem>
                {(f.options || []).filter((o) => o.value !== '').map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          <span className="text-xs text-[var(--ns-text-subtle)] sm:ml-auto">
            {loading ? 'Cargando…' : `${total} resultado${total === 1 ? '' : 's'}`}
          </span>
        </div>

        <Card className="bg-white border border-[var(--ns-border)] rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--ns-text-muted)]">
              Sin resultados. {canCreate ? <span>Crea el primero pulsando <strong>“Nuevo”</strong>.</span> : null}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-auto" data-testid={`${testIdPrefix}-table`}>
                <thead className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]">
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key} className={`text-left px-3 py-3 font-semibold ${c.className || ''}`}>{c.label}</th>
                    ))}
                    <th className="text-right px-3 py-3 font-semibold whitespace-nowrap">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-t border-[var(--ns-border)] hover:bg-[var(--ns-bg)]/40" data-testid={`${testIdPrefix}-row-${row.id}`}>
                      {columns.map((c) => (
                        <td key={c.key} className={`px-3 py-3 align-top ${c.className || ''}`}>
                          {c.render ? c.render(row) : (row[c.key] === null || row[c.key] === undefined ? '—' : String(row[c.key]))}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-right align-top whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-1">
                          {rowActions ? React.createElement(rowActions, { row }) : null}
                          <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-8 px-2 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15" data-testid={`${testIdPrefix}-edit-${row.id}`}>
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                          </Button>
                          {canDelete ? (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => askDelete(row)}
                              className="h-8 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                              data-testid={`${testIdPrefix}-delete-${row.id}`}
                              title="Eliminar"
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right" className="w-full sm:max-w-lg bg-white overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display" data-testid={`${testIdPrefix}-form-title`}>
                {editing ? `Editar ${title.toLowerCase().replace(/s$/, '')}` : `Nuevo ${title.toLowerCase().replace(/s$/, '')}`}
              </SheetTitle>
            </SheetHeader>
            <div className="py-4 space-y-4">
              {formFields.map((f) => (
                <FieldRenderer
                  key={f.name}
                  field={f}
                  value={form[f.name]}
                  onChange={(v) => {
                    setForm((prev) => ({ ...prev, [f.name]: v }));
                    // Al editar un campo con error, limpiamos su mensaje
                    // para dar feedback inmediato al usuario.
                    if (fieldErrors[f.name]) {
                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next[f.name];
                        return next;
                      });
                    }
                  }}
                  allValues={form}
                  error={fieldErrors[f.name]}
                />
              ))}
            </div>
            <SheetFooter className="gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="h-10 rounded-xl" data-testid={`${testIdPrefix}-form-cancel`}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white"
                data-testid={`${testIdPrefix}-form-submit`}
              >
                {submitting ? 'Guardando…' : (editing ? 'Guardar cambios' : 'Crear')}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <AlertDialogContent data-testid={`${testIdPrefix}-delete-dialog`}>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar este elemento?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteWarning ? deleteWarning : (
                  <>
                    Vas a eliminar <strong>{targetLabel || 'este registro'}</strong>.
                    {' '}La acción es reversible mediante soporte (eliminación lógica),
                    pero el elemento dejará de aparecer en los listados y en la web pública.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testIdPrefix}-delete-cancel`}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid={`${testIdPrefix}-delete-confirm`}
              >
                {deleting ? 'Eliminando…' : 'Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

export { StatusBadge };
